import { use, useEffect, useState } from 'react';
import { useHits } from 'react-instantsearch';
import SwipeableViews from 'react-swipeable-views';
import { CaretBack, CaretForward, Cube } from '@ghosts-inc/design-system/icons';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AnalyticsEventNames } from '@/analytics/Event.types';
import { AnalyticsPageNames } from '@/analytics/Page.types';
import { PageContext, SectionContext } from '@/analytics/analytics.types';
import { analytics } from '@/analytics/segment';
import ListingCard from '@/components/ListingCard';
import IconButtonBackground from '@/legacy/common/IconButtonBackground';
import { BuyerListingFavorite } from '@/legacy/favorites/FavoritesViewModel';
import { isNewListing, isTrendingListing } from '@/lib/algolia/helpers';
import { ListingHit } from '@/types/ListingHit';
import { Collection } from '@/types/collections';
import { Background } from '../types';
import { CollectionVisibilityContext } from './CollectionContext';
import SkeletonState from './SkeletonState';

const MINIMUM_RESULTS = 2;

const SeeMoreBox = ({
  analyticsInfo,
  navigateTo,
  seeMoreStringComponent,
  background,
  collection,
}: {
  analyticsInfo: {
    context: PageContext & SectionContext;
  };
  navigateTo: string;
  seeMoreStringComponent: JSX.Element;
  background?: Background;
  collection?: Collection;
}) => {
  const theme = useTheme();
  const isBelowDesktop = useMediaQuery((themeForHook: Theme) =>
    themeForHook.breakpoints.down('desktop'),
  );

  const textColor = background === 'dark' ? 'typographyDarkInverted' : 'typographyDarkPrimary';
  const borderColor =
    background === 'dark'
      ? theme.palette.surfaceInverted.main
      : theme.palette.action.disabledBackground;

  return (
    <Box width="100%" sx={{ aspectRatio: '1' }}>
      <Stack
        width="100%"
        border={`solid 1px ${borderColor}`}
        sx={{ aspectRatio: '1' }}
        alignItems="center"
        justifyContent="center"
      >
        <Stack
          width="100%"
          maxWidth={160}
          alignItems="center"
          justifyContent="space-evenly"
          sx={{ aspectRatio: '1' }}
          p={1}
        >
          {!isBelowDesktop && <Cube fontSize="large" color="surfaceInverted" />}
          <Typography textAlign="center" color={textColor}>
            {seeMoreStringComponent}
          </Typography>
          <Button
            data-testid={`view-all-button-${collection}`}
            href={navigateTo}
            onClick={() => {
              analytics.track(AnalyticsEventNames.HOME_SECTION_CAROUSEL_VIEW_MORE_CLICKED, {
                ...analyticsInfo,
              });
            }}
            color={background === 'dark' ? 'buttonLight' : 'buttonDark'}
            size="small"
          >
            View All
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default function SearchState({
  sectionTitle,
  seeMoreStringComponent,
  navigateTo,
  numPerPage,
  favorites,
  analyticsInfo,
  background,
  collection,
  isStockXSectionEnabled,
}: {
  sectionTitle: string;
  collection?: Collection;
  seeMoreStringComponent: JSX.Element;
  navigateTo: string;
  numPerPage: number;
  favorites?: BuyerListingFavorite[];
  analyticsInfo: {
    context: PageContext & SectionContext;
  };
  background?: Background;
  isStockXSectionEnabled: boolean;
}): JSX.Element | null {
  const [activeStep, setActiveStep] = useState(0);
  const { items, results } = useHits<ListingHit>();

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.only('mobile'));
  const isBelowDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.down('desktop'));

  // determines number of listings on each page of the carousel
  const num = isMobile ? 4 : isBelowDesktop ? 3 : numPerPage;
  const hasSecondPage = items.length > num;

  const firstPage = items.slice(0, num);
  const secondPage = items.slice(num, num * 2 - 1);

  const { setVisible } = use(CollectionVisibilityContext);

  const handleNext = () => {
    analytics.track(AnalyticsEventNames.HOME_SECTION_CAROUSEL_RIGHT_ARROW_CLICKED, {
      ...analyticsInfo,
      listingIds: secondPage.map((item) => item.objectID),
    });
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    analytics.track(AnalyticsEventNames.HOME_SECTION_CAROUSEL_LEFT_ARROW_CLICKED, {
      ...analyticsInfo,
      listingIds: firstPage.map((item) => item.objectID),
    });
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  useEffect(() => {
    if (results?.__isArtificial) return;
    setVisible(items.length >= MINIMUM_RESULTS);
  }, [results?.__isArtificial, items.length, setVisible]);

  if (results?.__isArtificial) {
    return <SkeletonState numPerPage={numPerPage} />;
  }

  if (items.length < MINIMUM_RESULTS) {
    // if there are no results, the CollectionVisibilityProvider will handle hiding it
    // but we want to show the skeleton state until that happens
    // otherwise it'll just show an empty space or a single result that's then hidden
    return <SkeletonState numPerPage={numPerPage} />;
  }

  let additionalBoxes = [] as number[];
  const diff = hasSecondPage ? num * 2 - items.length - 1 : num - items.length;
  additionalBoxes = Array.from({ length: diff }, (_, index) => index);

  return (
    <Stack position="relative" direction="row" alignItems="center">
      {isMobile ? (
        <Box overflow="auto">
          <Stack
            width={(firstPage.length + 1) * (numPerPage === 4 && !isMobile ? 294 : 240)}
            direction="row"
            gap={2}
            padding="1px"
          >
            {firstPage.map((hit: ListingHit) => (
              <ListingCard
                isStockXSectionEnabled={isStockXSectionEnabled}
                key={hit.objectID}
                hit={hit}
                analyticsInfo={{
                  new: isNewListing(hit),
                  trending: isTrendingListing(hit),
                  ...analyticsInfo,
                }}
                isFavorite={!!favorites?.some((f) => f.listingId === Number(hit.objectID))}
                background={background}
                isFirstPage
              />
            ))}
            <SeeMoreBox
              analyticsInfo={analyticsInfo}
              navigateTo={navigateTo}
              seeMoreStringComponent={seeMoreStringComponent}
              background={background}
              collection={collection}
            />
          </Stack>
        </Box>
      ) : (
        <>
          {hasSecondPage && (
            <Box sx={{ position: 'absolute', top: '40%', left: '-64px' }} hidden={activeStep === 0}>
              <IconButtonBackground onClick={handleBack} background={background}>
                <CaretBack color={background === 'dark' ? 'surfaceInverted' : undefined} />
              </IconButtonBackground>
            </Box>
          )}
          <Stack overflow="auto" flex={1} position="relative">
            <SwipeableViews enableMouseEvents index={activeStep} onChangeIndex={handleStepChange}>
              <Box>
                <Stack direction="row" gap={5}>
                  {firstPage.map((hit: ListingHit) => (
                    <ListingCard
                      isStockXSectionEnabled={isStockXSectionEnabled}
                      key={hit.objectID}
                      hit={hit}
                      isFavorite={!!favorites?.some((f) => f.listingId === Number(hit.objectID))}
                      background={background}
                      analyticsInfo={{
                        new: isNewListing(hit),
                        trending: isTrendingListing(hit),
                        context: {
                          page: AnalyticsPageNames.BUYER_HOME,
                          section: sectionTitle,
                        },
                      }}
                      isFirstPage
                    />
                  ))}
                  {!hasSecondPage ? (
                    additionalBoxes.map((a) => <Box key={a} width="100%" />)
                  ) : (
                    <></>
                  )}
                </Stack>
              </Box>
              {hasSecondPage ? (
                <Box>
                  <Stack direction="row" gap={5}>
                    {secondPage.map((hit: ListingHit) => (
                      <ListingCard
                        isStockXSectionEnabled={isStockXSectionEnabled}
                        key={hit.objectID}
                        hit={hit}
                        isFavorite={!!favorites?.some((f) => f.listingId === Number(hit.objectID))}
                        background={background}
                        analyticsInfo={{
                          new: isNewListing(hit),
                          trending: isTrendingListing(hit),
                          context: {
                            page: AnalyticsPageNames.BUYER_HOME,
                            section: sectionTitle,
                          },
                        }}
                      />
                    ))}
                    <SeeMoreBox
                      analyticsInfo={analyticsInfo}
                      seeMoreStringComponent={seeMoreStringComponent}
                      navigateTo={navigateTo}
                      background={background}
                      collection={collection}
                    />

                    {additionalBoxes.map((a) => (
                      <Box key={a} width="100%" />
                    ))}
                  </Stack>
                </Box>
              ) : (
                <></>
              )}
            </SwipeableViews>
          </Stack>
          {hasSecondPage && (
            <Box
              sx={{ position: 'absolute', top: '40%', right: '-64px' }}
              hidden={activeStep === 1}
            >
              <IconButtonBackground
                data-testid={`list-collection-next-${collection}`}
                onClick={handleNext}
                background={background}
              >
                <CaretForward color={background === 'dark' ? 'surfaceInverted' : undefined} />
              </IconButtonBackground>
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}
